import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { discardFamilyLabel, SYSTEM_FAILURE_FAMILIES } from "../lib/discardFamilies";
import { ActiveFiltersBar, type FiltroAttivo } from "../lib/ActiveFiltersBar";
import { correctedFieldLabel, correctionCategoryLabel } from "../lib/activityLabels";
import { fetchRequests, fetchRequestCount, fetchSearchSuggestions, checkNewRdi } from "../api/client";
import { StatusIcon } from "../lib/status";
import { LockChip } from "../lib/LockChip";
import type { SearchSuggestion } from "../api/client";
import { getOriginalSender, getCleanSubject, getOriginalDate } from "../helpers/emailMetadata";
import { DateRangePicker } from "../components/DateRangePicker";
import {
  ColHeader, useColumnFilterPopover, type ListaColonneCtx,
  PRIORITY_OPTIONS, CAUSALE_OPTIONS,
  prioritaCell, causaleCell, riferimentoCell, impiantoCell, confidenzaCell, allegatiCell,
  getAttachmentCount, hasAudioAttachment, formatDataBreve, formatDataEstesa,
} from "../lib/listaPratiche";
import { markSectionSeen, type Section } from "../hooks/useNewSince";
import type { ProcessingRequest, PaginatedResponse } from "../api/types";

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [20, 50, 100];
const REFRESH_INTERVAL = 30_000;

export const STATUS_OPTIONS = [
  { value: "all", label: "Tutti" },
  { value: "pending_review", label: "Da validare" },
  { value: "received", label: "In elaborazione" },
  // Per il backend "approvata" e' `done` OPPURE `auto_approved` (vedi il
  // commento sul multi-valore qui sotto, e i drill-down delle statistiche che
  // generano gia' `?status=auto_approved,done`). La pill ne chiedeva uno solo:
  // una pratica approvata automaticamente non compariva fra le approvate, e
  // dall'interfaccia risultava sparita.
  { value: "done,auto_approved", label: "Approvati" },
  { value: "discarded", label: "Scartati" },
];

interface RequestListProps {
  origin?: "mop";
  filterStatus?: string;
  subType?: string;
  excludeSubType?: string;
  section?: Section;
}

export function RequestList({ filterStatus, subType, excludeSubType, section, origin }: RequestListProps) {
  const navigate = useNavigate();
  const detailBasePath = origin === "mop" ? "/mop/requests" : "/review3";
  const [searchParams, setSearchParams] = useSearchParams();

  const status = filterStatus ?? (searchParams.get("status") || "pending_review");
  // `status` può contenere **più valori separati da virgola**: i drill-down delle
  // statistiche li generano così (`?status=auto_approved,done&had_corrections=true`,
  // perché per il backend "approvata" è `done` *oppure* `auto_approved`), e l'API li
  // accetta. Confrontarlo con `===` faceva fallire ogni test di uguaglianza: nessuna
  // pill si accendeva e i sotto-filtri, condizionati a `status === "done"` e
  // `status === "discarded"`, non venivano mai renderizzati. Risultato: la lista era
  // filtrata e non lo mostrava da nessuna parte. Verificato il 2026-08-04.
  const statusSet = new Set(status.split(",").map((s) => s.trim()).filter(Boolean));
  const includeStatus = (v: string) => statusSet.has(v);
  const dateFrom = searchParams.get("date_from") || "";
  const dateTo = searchParams.get("date_to") || "";
  const confMin = searchParams.get("conf_min") || "";
  const confMax = searchParams.get("conf_max") || "";
  const fId = searchParams.get("id") || "";
  const fPriority = searchParams.get("priority") || "";
  const fSubject = searchParams.get("q") || "";
  const fSender = searchParams.get("sender") || "";
  const fImpianto = searchParams.get("impianto") || "";
  const fCausale = searchParams.get("causale") || "";
  const fNumRif = searchParams.get("numero_riferimento") || "";
  const fDiscardType = searchParams.get("discard_type") || "";
  // Famiglia di scarto: ci si arriva cliccando una voce della pagina statistiche.
  const fDiscardFamily = searchParams.get("discard_family") || "";
  const fCorrectedField = searchParams.get("corrected_field") || "";
  const fCorrectionCategory = searchParams.get("correction_category") || "";
  const fHadCorrections = searchParams.get("had_corrections") || "";
  const sort = searchParams.get("sort") || "date_desc";
  const page = parseInt(searchParams.get("page") || "1", 10);
  const pageSize = parseInt(searchParams.get("size") || String(DEFAULT_PAGE_SIZE), 10);

  // Azzera il badge "nuovi" per questa sezione al mount
  useEffect(() => {
    if (section) markSectionSeen(section);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  // Quale filtro di colonna e' aperto (uno alla volta) — logica condivisa con
  // la dashboard Agente, vedi lib/listaPratiche.
  const { openFilter, setOpenFilter } = useColumnFilterPopover();

  // Evidenzia l'ultimo ticket visitato
  const [lastVisitedId, setLastVisitedId] = useState<string | null>(() => localStorage.getItem("lastVisitedId"));

  // ── Search bar state ──
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestIdx, setSuggestIdx] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestTimer = useRef<ReturnType<typeof setTimeout>>();
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // Sync searchText with URL param on mount / URL change
  useEffect(() => { setSearchText(fSubject); }, [fSubject]);

  // Close search dropdown on outside click
  useEffect(() => {
    if (!searchOpen) return;
    function handleClick(e: MouseEvent) {
      if (searchContainerRef.current?.contains(e.target as Node)) return;
      setSuggestions([]);
      if (!fSubject && !searchText) setSearchOpen(false);
    }
    const t = setTimeout(() => document.addEventListener("mousedown", handleClick), 0);
    return () => { clearTimeout(t); document.removeEventListener("mousedown", handleClick); };
  }, [searchOpen, fSubject, searchText]);

  function handleSearchInput(val: string) {
    setSearchText(val);
    setSuggestIdx(-1);
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    if (val.length < 2) { setSuggestions([]); return; }
    setSuggestLoading(true);
    suggestTimer.current = setTimeout(() => {
      fetchSearchSuggestions(val, origin)
        .then(r => setSuggestions(r))
        .catch(() => setSuggestions([]))
        .finally(() => setSuggestLoading(false));
    }, 300);
  }

  function applySearch(q: string) {
    setSuggestions([]);
    setFilter({ q: q.trim() || null });
    if (!q.trim()) { setSearchOpen(false); setSearchText(""); }
  }

  function handleSearchKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      setSuggestions([]);
      if (!fSubject) { setSearchOpen(false); setSearchText(""); }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSuggestIdx(i => Math.min(i + 1, suggestions.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSuggestIdx(i => Math.max(i - 1, -1));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (suggestIdx >= 0 && suggestions[suggestIdx]) {
        navigate(`${detailBasePath}/${suggestions[suggestIdx].id}`);
        setSuggestions([]);
      } else {
        applySearch(searchText);
      }
    }
  }

  function setFilter(updates: Record<string, string | null>) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v === null || v === "" || v === undefined) next.delete(k);
        else next.set(k, v);
      }
      if (!("page" in updates)) next.delete("page");
      return next;
    }, { replace: true });
  }

  const [data, setData] = useState<PaginatedResponse<ProcessingRequest> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [checking, setChecking] = useState(false);
  const [checkMsg, setCheckMsg] = useState<string | null>(null);
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const [countPending, setCountPending] = useState<number | null>(null);
  const [countToday, setCountToday] = useState<number | null>(null);
  const [countTotal, setCountTotal] = useState<number | null>(null);
  const [countConfLow, setCountConfLow] = useState<number | null>(null);
  const [countConfMed, setCountConfMed] = useState<number | null>(null);
  const [countConfHigh, setCountConfHigh] = useState<number | null>(null);
  const [countDiscardOperator, setCountDiscardOperator] = useState<number | null>(null);
  const [countDiscardAuto, setCountDiscardAuto] = useState<number | null>(null);
  const [countWithCorrections, setCountWithCorrections] = useState<number | null>(null);
  const [countWithoutCorrections, setCountWithoutCorrections] = useState<number | null>(null);

  const CONF_THRESHOLD_LOW = 0.50;   // 0 – 50%  → bassa (rosso)
  const CONF_THRESHOLD_MED = 0.80;   // 51 – 79% → media (giallo)
                                     // 80 – 100% → alta (verde)

  const loadCounts = useCallback(() => {
    const today = new Date().toISOString().slice(0, 10);
    const scope = { origin, sub_type: subType, exclude_sub_type: excludeSubType };

    // I conteggi CLICCABILI (bande confidence, sottofiltri scarto e modifiche)
    // promettono una lista: devono quindi essere calcolati sugli stessi filtri
    // attivi, altrimenti il click apre un elenco che non corrisponde al numero.
    // Difetto reale: con discard_family=errore_sistema la riga "Tipo scarto"
    // dichiarava Operatore (172) / Automatico (55) mentre la lista mostrava 7
    // righe, perche' lo scope portava solo sub_type/exclude_sub_type.
    //
    // Ogni conteggio NON fissa la dimensione che fa variare lui stesso: le bande
    // non passano la confidence, i sottofiltri scarto non passano discard_type,
    // quelli sulle modifiche non passano had_corrections.
    //
    // Nota: /requests/count non conosce mittente, impianto, causale, priorita' e
    // ricerca libera. Se uno di quelli e' attivo i conteggi restano piu' larghi
    // della lista — per chiuderla del tutto i due endpoint devono condividere la
    // costruzione del WHERE, oggi duplicata a mano.
    const clickScope = {
      ...scope,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      discard_family: fDiscardFamily || undefined,
    };

    fetchRequestCount({ status: "pending_review", ...scope }).then((r) => setCountPending(r.count)).catch(() => {});
    fetchRequestCount({ status: "done", date_from: today, date_field: "updated_at", ...scope }).then((r) => setCountToday(r.count)).catch(() => {});
    fetchRequestCount({ ...scope }).then((r) => setCountTotal(r.count)).catch(() => {});
    // Confidence bands — solo pending_review
    fetchRequestCount({ status: "pending_review", confidence_max: CONF_THRESHOLD_LOW, ...clickScope }).then((r) => setCountConfLow(r.count)).catch(() => {});
    fetchRequestCount({ status: "pending_review", confidence_min: CONF_THRESHOLD_LOW + 0.01, confidence_max: CONF_THRESHOLD_MED - 0.01, ...clickScope }).then((r) => setCountConfMed(r.count)).catch(() => {});
    fetchRequestCount({ status: "pending_review", confidence_min: CONF_THRESHOLD_MED, ...clickScope }).then((r) => setCountConfHigh(r.count)).catch(() => {});
    // Conteggi contestuali per sottofiltri
    fetchRequestCount({ status: "discarded", discard_type: "operator", ...clickScope }).then((r) => setCountDiscardOperator(r.count)).catch(() => {});
    fetchRequestCount({ status: "discarded", discard_type: "auto", ...clickScope }).then((r) => setCountDiscardAuto(r.count)).catch(() => {});
    fetchRequestCount({ status: "done", had_corrections: true, ...clickScope }).then((r) => setCountWithCorrections(r.count)).catch(() => {});
    fetchRequestCount({ status: "done", had_corrections: false, ...clickScope }).then((r) => setCountWithoutCorrections(r.count)).catch(() => {});
  }, [origin, subType, excludeSubType, dateFrom, dateTo, fDiscardFamily]);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  const load = useCallback(() => {
    setIsLoading(true);
    fetchRequests({
      origin,
      status: (status === "all" || !status) ? undefined : status,
      q: fSubject || undefined,
      id: fId || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      confidence_min: confMin ? parseFloat(confMin) / 100 : undefined,
      confidence_max: confMax ? parseFloat(confMax) / 100 : undefined,
      priority: fPriority || undefined,
      sender: fSender || undefined,
      impianto: fImpianto || undefined,
      causale: fCausale || undefined,
      numero_riferimento: fNumRif || undefined,
      sub_type: subType,
      exclude_sub_type: excludeSubType,
      discard_type: fDiscardType || undefined,
      discard_family: fDiscardFamily || undefined,
      corrected_field: fCorrectedField || undefined,
      correction_category: fCorrectionCategory || undefined,
      had_corrections: fHadCorrections === "true" ? true : fHadCorrections === "false" ? false : undefined,
      sort,
      page,
      size: pageSize,
    })
      .then((d) => { setData(d); setError(null); setLastRefresh(Date.now()); })
      .catch((e) => setError(e.message))
      .finally(() => setIsLoading(false));
  }, [origin, status, fId, fSubject, dateFrom, dateTo, confMin, confMax, fPriority, fSender, fImpianto, fCausale, fNumRif, fDiscardType, fDiscardFamily, fCorrectedField, fCorrectionCategory, fHadCorrections, subType, excludeSubType, sort, page, pageSize]);

  // "Forza aggiornamento": forza subito l'ingestione della mailbox RDI (come
  // il tasto ricevi di un client email), poi ricarica lista + contatori.
  const handleCheckNew = useCallback(async () => {
    setChecking(true);
    setCheckMsg(null);
    try {
      const r = await checkNewRdi();
      setCheckMsg(r.error ? `Errore: ${r.error}` : r.new > 0 ? `${r.new} nuove richieste` : "Nessuna nuova email");
      load();
      loadCounts();
    } catch {
      setCheckMsg("Errore nel controllo");
    } finally {
      setChecking(false);
      setTimeout(() => setCheckMsg(null), 5000);
    }
  }, [load, loadCounts]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(() => { load(); loadCounts(); }, REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, [load, loadCounts]);
  useEffect(() => {
    const id = setInterval(() => setSecondsAgo(Math.floor((Date.now() - lastRefresh) / 1000)), 1000);
    return () => clearInterval(id);
  }, [lastRefresh]);

  function setTextFilter(key: string, value: string) {
    if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]);
    debounceTimers.current[key] = setTimeout(() => setFilter({ [key]: value || null }), 300);
  }

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  function getTicketCount(item: ProcessingRequest): number {
    // Multi-ticket post-split: ogni record è un ticket. total_tickets è il N
    // della serie di sibling generati dalla stessa email originale.
    if (typeof item.total_tickets === "number" && item.total_tickets > 0) {
      return item.total_tickets;
    }
    const analyses = item.analyses;
    if (Array.isArray(analyses)) return analyses.length;
    return 1;
  }

  function getTicketIndex(item: ProcessingRequest): number {
    return typeof item.ticket_index === "number" ? item.ticket_index : 0;
  }

  // Intestazione di colonna (ordinamento + popover di filtro) e celle
  // vengono da lib/listaPratiche: le condivide con la dashboard Agente.
  const colCtx: ListaColonneCtx = {
    sort, setFilter, setTextFilter, openFilter, setOpenFilter, confMin, confMax,
  };

  function getStatusIcon(s: string) {
    return <StatusIcon status={s} />;
  }

  const hasAnyColumnFilter = fSubject || fSender || fImpianto || fPriority || confMin || confMax || fNumRif;
  const SUBTYPE_TITLES: Record<string, string> = { allarme: "Allarmi", hyperfabbricati: "Hyperfabbricati" };
  const title = origin === "mop" ? "Coda di lavoro MOP" : subType && SUBTYPE_TITLES[subType] ? SUBTYPE_TITLES[subType] : filterStatus === "done" ? "Approvati" : filterStatus === "discarded" ? "Scartati" : "Coda di Lavoro";

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--space-3)", gap: "var(--space-3)", rowGap: "var(--space-2)" }}>
        <h1 className="page-title" style={{ flexShrink: 0 }}>{title}</h1>

        {/* ── Search bar (sempre visibile) ── */}
        <div ref={searchContainerRef} style={{ position: "relative", flex: 1, maxWidth: 480, minWidth: 0 }}>
          <div style={{ position: "relative" }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 6,
              border: fSubject ? "1.5px solid var(--indaco-brand-primary)" : "1px solid var(--indaco-gray-300)",
              borderRadius: "var(--border-radius-md)", background: fSubject ? "var(--indaco-brand-primary-light)" : "var(--indaco-white)", padding: "5px 12px",
              boxShadow: suggestions.length > 0 ? "var(--shadow-md)" : "none",
              transition: "border-color .15s, background .15s, box-shadow .15s",
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 18, color: fSubject ? "var(--indaco-brand-primary)" : "var(--indaco-gray-400)", flexShrink: 0 }}>search</span>
              <input ref={searchInputRef} type="text" value={searchText}
                placeholder="Cerca: oggetto, mittente, motivo, impianto, indirizzo..."
                onChange={e => handleSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => { if (searchText.length >= 2 && suggestions.length === 0) handleSearchInput(searchText); }}
                style={{
                  flex: 1, border: "none", outline: "none", background: "transparent",
                  fontSize: 13, padding: "3px 0", minWidth: 0,
                }} />
              {suggestLoading && <span className="spinner" style={{ width: 14, height: 14, flexShrink: 0 }} />}
              {(searchText || fSubject) && (
                <button onClick={() => { setSearchText(""); setSuggestions([]); applySearch(""); }}
                  style={{ display: "flex", alignItems: "center", border: "none", background: "none", cursor: "pointer", padding: 2, flexShrink: 0 }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-gray-400)" }}>close</span>
                </button>
              )}
            </div>
            {/* Suggestions dropdown */}
            {suggestions.length > 0 && (
              <div style={{
                position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 200,
                background: "var(--indaco-white)", border: "1px solid var(--indaco-gray-200)", borderRadius: "var(--border-radius-lg)",
                boxShadow: "var(--shadow-lg)", overflow: "hidden", maxHeight: 360, overflowY: "auto",
              }}>
                {suggestions.map((s, i) => (
                  <div key={s.id}
                    onMouseDown={() => { navigate(`${detailBasePath}/${s.id}`); setSuggestions([]); }}
                    onMouseEnter={() => setSuggestIdx(i)}
                    style={{
                      padding: "8px 12px", cursor: "pointer", fontSize: "var(--font-size-sm)",
                      background: i === suggestIdx ? "var(--indaco-gray-50)" : "transparent",
                      borderBottom: i < suggestions.length - 1 ? "1px solid var(--indaco-gray-100)" : "none",
                    }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontWeight: 600, fontSize: 11, color: "var(--indaco-gray-400)", fontFamily: "monospace", flexShrink: 0 }}>
                        {s.id.slice(0, 8)}
                      </span>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                        {s.subject || "(senza oggetto)"}
                      </span>
                      {s.impianto && (
                        <code style={{ fontSize: 10, color: "var(--indaco-gray-500)", flexShrink: 0 }}>{s.impianto}</code>
                      )}
                    </div>
                    {(s.motivo || s.sender) && (
                      <div style={{ fontSize: 11, color: "var(--indaco-gray-400)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {s.motivo || s.sender}
                      </div>
                    )}
                  </div>
                ))}
                <div style={{ padding: "6px 12px", fontSize: 11, color: "var(--indaco-gray-400)", background: "var(--indaco-gray-50)", textAlign: "center" }}>
                  Invio per cercare in tutti i risultati
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", flexShrink: 0 }}>
          {hasAnyColumnFilter && (
            <button className="btn btn-ghost btn-sm" title="Pulisci tutti i filtri"
              onClick={() => { setFilter({ q: null, sender: null, impianto: null, priority: null, conf_min: null, conf_max: null, numero_riferimento: null }); setSearchText(""); setSearchOpen(false); }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-danger)" }}>filter_list_off</span>
            </button>
          )}
          {checkMsg && <span className="text-xs" style={{ color: "var(--indaco-brand-primary)" }}>{checkMsg}</span>}
          <button className="btn btn-outline btn-sm" onClick={handleCheckNew} disabled={checking}
            title="Controlla subito nuove email nella casella (forza aggiornamento)"
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            {checking
              ? <span className="spinner" style={{ width: 14, height: 14 }} />
              : <span className="material-symbols-outlined" style={{ fontSize: 16 }}>mark_email_unread</span>}
            <span>Forza aggiornamento</span>
          </button>
          <span className="text-muted text-xs">{secondsAgo < 5 ? "aggiornato ora" : `${secondsAgo}s fa`}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => { load(); loadCounts(); }} title="Aggiorna">
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>refresh</span>
          </button>
        </div>
      </div>

      {/* Counters */}
      <div style={{ display: "flex", gap: "var(--space-4)", marginBottom: "var(--space-3)", fontSize: "var(--font-size-sm)", alignItems: "center", flexWrap: "wrap" }}>
        <span><span className="text-muted">Da validare: </span><strong style={{ color: "var(--indaco-warning)" }}>{countPending ?? "\u2014"}</strong></span>
        <span style={{ color: "var(--indaco-gray-300)" }}>|</span>
        <span><span className="text-muted">Processati oggi: </span><strong style={{ color: "var(--indaco-success)" }}>{countToday ?? "\u2014"}</strong></span>
        <span style={{ color: "var(--indaco-gray-300)" }}>|</span>
        <span><span className="text-muted">Totale: </span><strong>{countTotal ?? "\u2014"}</strong></span>

        <span style={{ color: "var(--indaco-gray-300)", margin: "0 var(--space-1)" }}>│</span>
        <span className="text-muted" style={{ fontSize: "var(--font-size-xs)" }}>Confidence:</span>
        {([
          { label: "Bassa", count: countConfLow, color: "var(--indaco-danger)", lightColor: "var(--indaco-danger-light)", min: "0", max: "50" },
          { label: "Media", count: countConfMed, color: "var(--indaco-warning)", lightColor: "var(--indaco-warning-light)", min: "51", max: "79" },
          { label: "Alta", count: countConfHigh, color: "var(--indaco-success)", lightColor: "var(--indaco-success-light)", min: "80", max: "100" },
        ] as const).map((band) => {
          const isActive = confMin === band.min && confMax === band.max;
          return (
            <button key={band.label}
              onClick={() => {
                if (isActive) {
                  setFilter({ conf_min: null, conf_max: null, status: "pending_review" });
                } else {
                  setFilter({ conf_min: band.min, conf_max: band.max, status: "pending_review" });
                }
              }}
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                padding: "2px 10px", borderRadius: 12, fontSize: "var(--font-size-xs)", fontWeight: 600,
                border: isActive ? `2px solid ${band.color}` : "1px solid var(--indaco-gray-200)",
                background: isActive ? band.lightColor : "var(--indaco-gray-50)",
                color: band.color, cursor: "pointer",
                transition: "all .15s ease",
              }}
              title={`Filtra: confidence ${band.min}–${band.max}%`}
            >
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: band.color, display: "inline-block" }} />
              {band.count ?? "—"}
            </button>
          );
        })}
      </div>

      {/* Filtro arrivato da un drill-down delle statistiche. Senza questo banner
          la lista apparirebbe misteriosamente ridotta e non ci sarebbe modo di
          capire perche' ne' di togliere il filtro. */}
      {(() => {
        // Ogni filtro che arriva da un drill-down si dichiara qui. Prima ne
        // comparivano tre su cinque: `had_corrections` e `discard_type` erano letti
        // dall'URL, passati all'API e mai mostrati, quindi la lista risultava
        // filtrata senza un solo indizio del perché.
        const attivi: FiltroAttivo[] = [];
        if (fDiscardFamily) {
          attivi.push({
            chiave: "discard_family",
            etichetta: "Famiglia di scarto",
            valore: discardFamilyLabel(fDiscardFamily),
            grave: SYSTEM_FAILURE_FAMILIES.has(fDiscardFamily),
          });
        }
        if (fCorrectedField) {
          attivi.push({
            chiave: "corrected_field",
            etichetta: "Campo corretto",
            valore: correctedFieldLabel(fCorrectedField),
          });
        }
        if (fCorrectionCategory) {
          attivi.push({
            chiave: "correction_category",
            etichetta: "Motivo della correzione",
            valore: correctionCategoryLabel(fCorrectionCategory),
          });
        }
        if (fHadCorrections) {
          attivi.push({
            chiave: "had_corrections",
            etichetta: "Modifiche manuali",
            valore: fHadCorrections === "true" ? "Con modifiche" : "Senza modifiche",
          });
        }
        if (fDiscardType) {
          attivi.push({
            chiave: "discard_type",
            etichetta: "Scartata da",
            valore: fDiscardType === "operator" ? "Operatore" : "Pipeline",
          });
        }
        return (
          <ActiveFiltersBar
            filtri={attivi}
            onRemove={(chiavi) => setFilter(Object.fromEntries(chiavi.map((k) => [k, null])))}
          />
        );
      })()}

      {/* Top bar: status pills + urgente + date range */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--space-3)", flexWrap: "wrap", gap: "var(--space-2)" }}>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", alignItems: "center" }}>
          {!filterStatus && STATUS_OPTIONS.map((opt) => (
            <button key={opt.value} className={`btn btn-sm ${includeStatus(opt.value) ? "btn-primary" : "btn-outline"}`}
              onClick={() => setFilter({ status: opt.value || null, discard_type: null, had_corrections: null })}>{opt.label}</button>
          ))}
          <button
            className={`btn btn-sm ${fPriority === "alta" ? "btn-danger" : "btn-outline"}`}
            title="Filtra solo ticket priorità alta"
            onClick={() => setFilter({ priority: fPriority === "alta" ? null : "alta" })}>
            <span className="material-symbols-outlined" style={{ fontSize: 13, verticalAlign: "middle", marginRight: 2 }}>priority_high</span>
            Alta priorità
          </button>
        </div>
        <DateRangePicker from={dateFrom} to={dateTo}
          onChange={(f, t) => setFilter({ date_from: f, date_to: t })} />
      </div>

      {/* Sottofiltro scartate: operatore vs automatico */}
      {(filterStatus === "discarded" || includeStatus("discarded")) && (
        <div style={{ display: "flex", gap: "var(--space-2)", marginBottom: "var(--space-3)", alignItems: "center" }}>
          <span className="text-muted text-xs">Tipo scarto:</span>
          {[
            { value: "", label: "Tutti", count: null as number | null },
            { value: "operator", label: "Operatore", count: countDiscardOperator },
            { value: "auto", label: "Automatico", count: countDiscardAuto },
          ].map((opt) => (
            <button key={opt.value}
              className={`btn btn-sm ${fDiscardType === opt.value ? "btn-primary" : "btn-outline"}`}
              onClick={() => setFilter({ discard_type: opt.value || null })}>
              {opt.label}{opt.count != null ? ` (${opt.count})` : ""}
            </button>
          ))}
        </div>
      )}

      {/* Sottofiltro approvate: con/senza modifiche manuali */}
      {(filterStatus === "done" || includeStatus("done")) && (
        <div style={{ display: "flex", gap: "var(--space-2)", marginBottom: "var(--space-3)", alignItems: "center" }}>
          <span className="text-muted text-xs">Modifiche:</span>
          {[
            { value: "", label: "Tutte", count: null as number | null },
            { value: "true", label: "Con modifica", count: countWithCorrections },
            { value: "false", label: "Senza modifica", count: countWithoutCorrections },
          ].map((opt) => (
            <button key={opt.value}
              className={`btn btn-sm ${fHadCorrections === opt.value ? "btn-primary" : "btn-outline"}`}
              onClick={() => setFilter({ had_corrections: opt.value || null })}>
              {opt.label}{opt.count != null ? ` (${opt.count})` : ""}
            </button>
          ))}
        </div>
      )}

      {isLoading && !data && <div className="loading-center"><div className="spinner spinner-lg" /></div>}

      {error && (
        <div className="card" style={{ background: "var(--indaco-danger-light)", color: "var(--indaco-danger)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4 }}>error</span> Errore: {error}
        </div>
      )}

      {/* Table */}
      {data && data.items.length > 0 && (
        <div className="card" style={{ padding: 0, opacity: isLoading ? 0.6 : 1, transition: "opacity 150ms" }}>
          <div className="table-container">
            <table style={{ tableLayout: "fixed", width: "100%", minWidth: 860 }}>
              <thead>
                <tr>
                  <ColHeader ctx={colCtx} label="" sortKey="status" filterKey="status" filterType="select"
                    filterValue={status} options={STATUS_OPTIONS} style={{ width: 40, textAlign: "center" }} />
                  <ColHeader ctx={colCtx} label="ID" sortKey="id" filterKey="id" filterType="text" filterValue={fId}
                    style={{ width: 62 }} />
                  <ColHeader ctx={colCtx} label="" sortKey="priority" filterKey="priority" filterType="select"
                    filterValue={fPriority} options={PRIORITY_OPTIONS} style={{ width: 28, textAlign: "center" }} />
                  <ColHeader ctx={colCtx} label="Oggetto" sortKey="subject" filterKey="q" filterType="text"
                    filterValue={fSubject} style={{ width: 240 }} />
                  <ColHeader ctx={colCtx} label="Causale" sortKey="causale" filterKey="causale" filterType="select"
                    filterValue={fCausale} options={CAUSALE_OPTIONS} style={{ width: 52, textAlign: "center" }} />
                  <ColHeader ctx={colCtx} label="Rif." sortKey="numero_riferimento" filterKey="numero_riferimento"
                    filterType="text" filterValue={fNumRif} style={{ width: 72 }} />
                  <ColHeader ctx={colCtx} label="Mittente" sortKey="sender" filterKey="sender" filterType="text"
                    filterValue={fSender} style={{ width: 160 }} />
                  <ColHeader ctx={colCtx} label="Impianto" sortKey="impianto" filterKey="impianto" filterType="text"
                    filterValue={fImpianto} style={{ width: 90 }} />
                  <ColHeader ctx={colCtx} label="Conf." sortKey="confidence" filterKey="confidence" filterType="range"
                    filterValue={confMin || confMax ? `${confMin}-${confMax}` : ""} style={{ width: 52 }} />
                  <ColHeader ctx={colCtx} label="" sortKey="attachments" style={{ width: 26, textAlign: "center" }} />
                  <ColHeader ctx={colCtx} label="Data" sortKey="date" style={{ width: 82 }} />
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => {
                  return (
                    <tr key={item.id}
                      style={item.id === lastVisitedId ? { background: "var(--indaco-brand-primary-light)" } : undefined}
                      onClick={() => { setLastVisitedId(item.id); localStorage.setItem("lastVisitedId", item.id); navigate(`${detailBasePath}/${item.id}`); }}>
                      <td title={item.id} style={{ textAlign: "center", overflow: "hidden" }}>{getStatusIcon(item.status)}</td>
                      <td style={{ overflow: "hidden" }}>
                        <code className="truncate" style={{ display: "block", color: "var(--indaco-gray-600)",
                          fontSize: "var(--font-size-xs)" }} title={item.id}>{item.id.slice(0, 6)}</code>
                      </td>
                      <td style={{ textAlign: "center", overflow: "hidden" }}>{prioritaCell(item)}</td>
                      <td style={{ overflow: "hidden" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                          {hasAudioAttachment(item) && (
                            <span className="material-symbols-outlined" title="Messaggio vocale"
                              style={{ fontSize: 14, color: "var(--indaco-brand-primary)", flexShrink: 0 }}>mic</span>
                          )}
                          <span className="truncate" style={{ flex: 1, minWidth: 0, fontSize: "var(--font-size-sm)" }}>{getCleanSubject(item) || "\u2014"}</span>
                          {getTicketCount(item) > 1 && (
                            <span className="badge badge-warning badge-sm"
                              title={`Ticket ${getTicketIndex(item) + 1} di ${getTicketCount(item)} di questa email`}
                              style={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                              {getTicketIndex(item) + 1}/{getTicketCount(item)}
                            </span>
                          )}
                          {/* Lock attivo (punto 4, 2026-07-15): mostra chi tiene in lavorazione la
                              pratica (entro il TTL di 5 min), così si sa a chi chiedere di sganciarsi. */}
                          <LockChip lockedBy={item.locked_by} lockedByName={item.locked_by_name} lockedAt={item.locked_at} />
                        </div>
                      </td>
                      <td style={{ overflow: "hidden", textAlign: "center" }}>{causaleCell(item)}</td>
                      <td style={{ overflow: "hidden" }}>{riferimentoCell(item)}</td>
                      <td style={{ overflow: "hidden" }}>
                        <span className="truncate" style={{ display: "block", fontSize: "var(--font-size-sm)" }}>
                          {getOriginalSender(item) || "\u2014"}
                        </span>
                      </td>
                      <td style={{ overflow: "hidden" }}>{impiantoCell(item)}</td>
                      <td style={{ overflow: "hidden", textAlign: "right" }}>{confidenzaCell(item)}</td>
                      <td style={{ textAlign: "center", overflow: "hidden" }}>{allegatiCell(item)}</td>
                      <td style={{ whiteSpace: "nowrap", overflow: "hidden" }} className="text-sm">
                        {(() => {
                          const d = getOriginalDate(item);
                          return d ? <span title={formatDataEstesa(d)}>{formatDataBreve(d)}</span> : "\u2014";
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data && data.items.length === 0 && !isLoading && (
        <div className="card text-center text-muted" style={{ padding: "var(--space-10)" }}>Nessuna richiesta trovata</div>
      )}

      {/* Pagination */}
      {data && data.total > 0 && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "var(--space-3)", gap: "var(--space-2)" }}>
          {/* Page buttons */}
          {totalPages > 1 && (
            <div className="pagination">
              <button disabled={page <= 1} onClick={() => setFilter({ page: String(page - 1) })}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>chevron_left</span>
              </button>
              {(() => {
                const pages: (number | "...")[] = [];
                if (totalPages <= 7) {
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                } else {
                  pages.push(1);
                  if (page > 3) pages.push("...");
                  const start = Math.max(2, page - 1);
                  const end = Math.min(totalPages - 1, page + 1);
                  for (let i = start; i <= end; i++) pages.push(i);
                  if (page < totalPages - 2) pages.push("...");
                  pages.push(totalPages);
                }
                return pages.map((p, i) =>
                  p === "..." ? <span key={`e${i}`} className="text-muted" style={{ padding: "0 4px" }}>…</span>
                    : <button key={p} className={page === p ? "active" : ""}
                        onClick={() => setFilter({ page: p === 1 ? null : String(p) })}>{p}</button>
                );
              })()}
              <button disabled={page >= totalPages} onClick={() => setFilter({ page: String(page + 1) })}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>chevron_right</span>
              </button>
            </div>
          )}
          {/* Page size selector */}
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", fontSize: "var(--font-size-sm)" }}>
            <span className="text-muted">Mostra</span>
            <select className="form-input" value={pageSize}
              style={{ width: 70, padding: "2px 6px", fontSize: "var(--font-size-sm)" }}
              onChange={(e) => setFilter({ size: e.target.value === String(DEFAULT_PAGE_SIZE) ? null : e.target.value, page: null })}>
              {PAGE_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <span className="text-muted">di {data.total}</span>
          </div>
        </div>
      )}
    </div>
  );
}
